<template>
  <AdminLayout>
    <div class="d-flex justify-content-between align-items-center mb-4" style="flex-wrap:wrap;gap:0.75rem">
      <div class="d-flex align-items-center gap-3">
        <RouterLink to="/admin/content" class="back-link">
          <i class="ti ti-arrow-left"></i> All Sections
        </RouterLink>
        <h2 class="text-white mb-0 text-capitalize" style="font-weight:800">{{ sectionLabel }}</h2>
      </div>
      <button class="btn" :disabled="saving" @click="save">
        <span class="border-style">{{ saving ? "Saving..." : "Save Changes" }}</span>
      </button>
    </div>

    <div v-if="saved" class="admin-alert admin-alert-success mb-4">Changes saved successfully.</div>
    <div v-if="error" class="admin-alert admin-alert-danger mb-4">{{ error }}</div>

    <div v-if="loading" class="text-silver">Loading...</div>

    <div v-else class="admin-card">
      <ContentFieldEditor v-model="content" />
    </div>
  </AdminLayout>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import ContentFieldEditor from "@/components/admin/ContentFieldEditor.vue";
import AdminLayout from "@/components/admin/AdminLayout.vue";
import api from "@/services/api";

const LABELS: Record<string, string> = {
  navigation:    "Navigation & Logo",
  footer:        "Footer",
  banner:        "Main Banner",
  who_we_are:    "Who We Are",
  features:      "Our Features",
  our_content:   "Our Content",
  favorite_match:"Favorite Match",
  test_service:  "Test Our Service",
  benefits:      "Only Benefits",
  testimonials:  "Testimonials",
  fan_facts:     "Fan Facts / Stats",
  movies_slider: "Movies Slider",
  faq:           "FAQ Page",
  pricing:       "Pricing Card Images",
  about:         "About Us Page",
  contact:       "Contact Us Page",
};

// Ensures all image fields are visible in the editor even if DB hasn't been re-seeded
const SECTION_DEFAULTS: Record<string, Record<string, any>> = {
  banner: {
    cta_text: "",
    cta_url: "",
    logo_image_url: "",
    partner_logos:  Array.from({ length: 5 }, () => ({ image_url: "" })),
    slide_row_1:    Array.from({ length: 4 }, () => ({ image_url: "" })),
    slide_row_2:    Array.from({ length: 4 }, () => ({ image_url: "" })),
    slide_row_3:    Array.from({ length: 5 }, () => ({ image_url: "" })),
  },
  navigation:    { logo_url: "" },
  footer:        { logo_url: "" },
  who_we_are:    { image_1_url: "", image_2_url: "" },
  our_content:   { image_url: "" },
  test_service:  { background_image_url: "" },
  benefits:      { image_url: "" },
  faq:           { image_url: "" },
  favorite_match: {
    bg_shape_url: "",
    items: [
      { title: "", button_text: "", link: "", image_url: "", shape_url: "" },
      { title: "", button_text: "", link: "", image_url: "", shape_url: "" },
    ],
  },
  pricing: {
    bg_images:   Array.from({ length: 3 }, () => ({ image_url: "" })),
    card_images: Array.from({ length: 3 }, () => ({ image_url: "" })),
  },
  movies_slider: {
    top_title: "",
    heading: "",
    tagline: "",
    cta_text: "",
    cta_url: "",
    slides: Array.from({ length: 12 }, () => ({ image_url: "" })),
  },
  testimonials: {
    reviews_url: "",
    reviews_label: "",
    items: [{ name: "", review: "", image_url: "", rating: 5 }],
  },
  contact: {
    heading: "", subheading: "", email: "", phone: "", whatsapp_number: "", telegram: "", address: "", hours: "",
  },
  about: {
    heading: "", subheading: "", body: "",
    stats:    [{ value: "", label: "" }],
    features: [""],
  },
};

// Merges default fields into DB content — DB values win, but any missing keys are added from defaults.
// For arrays of objects, each item is merged with the default item template to add missing keys.
function mergeWithDefaults(
  defaults: Record<string, any>,
  db: Record<string, any>
): Record<string, any> {
  const result: Record<string, any> = { ...defaults };
  for (const key in db) {
    const dbVal = db[key];
    const defVal = defaults[key];
    const defIsObjArray =
      Array.isArray(defVal) && defVal.length > 0 && typeof defVal[0] === "object" && defVal[0] !== null;
    // If DB has an empty array but default has object-items, keep defaults so upload fields remain visible
    if (Array.isArray(dbVal) && dbVal.length === 0 && defIsObjArray) {
      continue;
    }
    if (Array.isArray(dbVal) && dbVal.length > 0 && defIsObjArray) {
      const template = defVal[0];
      result[key] = dbVal.map((item: any) =>
        typeof item === "object" && item !== null
          ? { ...template, ...item }
          : item
      );
    } else {
      result[key] = dbVal;
    }
  }
  return result;
}

export default defineComponent({
  name: "ContentEditorPage",
  components: { ContentFieldEditor, AdminLayout },
  setup() {
    const route = useRoute();
    const section = computed(() => route.params.section as string);
    const sectionLabel = computed(() => LABELS[section.value] || section.value.replace(/_/g, " "));

    const content = ref<Record<string, any>>({});
    const loading = ref(true);
    const saving = ref(false);
    const saved = ref(false);
    const error = ref("");

    onMounted(async () => {
      try {
        const { data } = await api.get(`/api/v1/content/${section.value}`);
        const db = JSON.parse(JSON.stringify(data.content));
        const defaults = SECTION_DEFAULTS[section.value] || {};
        content.value = mergeWithDefaults(defaults, db);
      } finally {
        loading.value = false;
      }
    });

    async function save() {
      saving.value = true;
      saved.value = false;
      error.value = "";
      try {
        await api.put(`/api/admin/content/${section.value}`, { content: content.value });
        saved.value = true;
        setTimeout(() => (saved.value = false), 3000);
      } catch (e: any) {
        error.value = e?.response?.data?.message || "Save failed.";
      } finally {
        saving.value = false;
      }
    }

    return { section, sectionLabel, content, loading, saving, saved, error, save };
  },
});
</script>

<style scoped>
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  color: #c0c0c0;
  text-decoration: none;
  font-size: 0.88rem;
  transition: color 0.2s;
}
.back-link:hover { color: #fff; }

.admin-card {
  background: #201516;
  border: 1px solid #3a2526;
  border-radius: 12px;
  padding: 2rem;
}

.admin-alert {
  padding: 0.85rem 1.25rem;
  border-radius: 8px;
  font-size: 0.9rem;
}
.admin-alert-success {
  background: rgba(25,135,84,0.15);
  color: #4ade80;
  border: 1px solid rgba(25,135,84,0.3);
}
.admin-alert-danger {
  background: rgba(201,14,14,0.15);
  color: #f87171;
  border: 1px solid rgba(201,14,14,0.3);
}
</style>
